import { AntDesign } from "@expo/vector-icons";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { router } from "expo-router";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { View, ScrollView, Pressable } from "react-native";
import { useDispatch, useSelector } from "react-redux";

import Logo from "@/assets/logo.svg";
import Copyright from "@/components/Copyright";
import Loader from "@/components/Loader";
import ProgressBarComponent from "@/components/ProgressBarComponent";
import Title from "@/components/Title";
import Button from "@/components/button/Button";
import ButtonLogout from "@/components/button/ButtonLogout";
import Input from "@/components/input/Input";
import InputDropList from "@/components/input/InputDropList";
import InputSelection from "@/components/input/InputSelection";
import { setForm } from "@/context/actions/actions";
import { useAppSelector } from "@/context/hooks";
import { storage } from "@/context/storage";
import { sendRequest } from "@/utils/ApiUtil";
import { CustomerDetailsIndividualObject } from "@/utils/dto/application.request";

type FormData = {
  taxReferenceNumber: string;
  passportNumber: string;
  citizenship: string[];
  nationality: string;
};

interface CountryData {
  country: string;
  translation: string;
  code: string;
  svg: string;
  idd: string;
}

export default function Nationality() {
  const [searchTerm, setSearchTerm] = useState("");
  const [citizenshipSearchTerm, setCitizenshipSearchTerm] = useState("");
  const [countries, setCountries] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [countriesCitizenship, setCountriesCitizenship] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const [inputArray, setInputArray] = useState<{
    [key: string]: { value: string; code: string }[];
  }>({});

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const globalValues = useSelector((state: any) => state);

  useEffect(() => {
    const fetchData = async () => {
      if (globalValues.isIndividualReviewing) {
        setValue(
          "taxReferenceNumber",
          globalValues.individualApplicationComplete[0].taxReferenceNumber
        );
        setValue(
          "passportNumber",
          globalValues.individualApplicationComplete[0].passportNumber
        );
        setValue(
          "citizenship",
          globalValues.individualApplicationComplete[0].citizenship
        );
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const citizenshipCodes = inputArray.citizenship?.map((item) => item.code);
    if (citizenshipCodes && citizenshipCodes.length > 0) {
      setValue("citizenship", citizenshipCodes);
    }
  }, [inputArray]);

  const resolver = classValidatorResolver(CustomerDetailsIndividualObject, {
    validator: { skipMissingProperties: true },
  });

  const { control, handleSubmit, formState, setValue } = useForm<FormData>({
    mode: "onSubmit",
    resolver,
  });

  const { isSubmitting } = formState;

  const states = useAppSelector((state: any) => state);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/countries", {
          // eslint-disable-next-line object-shorthand
          searchTerm: searchTerm,
        });
        const mappedCountries = response.map((country: CountryData) => ({
          value: country.country,
          code: country.code,
          uniqueKey: `country-${country.code}`,
        }));
        setCountries(mappedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    if (searchTerm) {
      fetchData();
    }
  }, [searchTerm]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await sendRequest("get", "/countries", {
          // eslint-disable-next-line object-shorthand
          searchTerm: citizenshipSearchTerm,
        });
        const mappedCountries = response.map((country: CountryData) => ({
          value: country.country,
          code: country.code,
          uniqueKey: `country-${country.code}`,
        }));
        setCountriesCitizenship(mappedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    if (citizenshipSearchTerm) {
      fetchData();
    }
  }, [citizenshipSearchTerm]);

  const onSubmit = async (data: FormData) => {
    const type = storage.getString("user.auth.applicationType");

    dispatch(setForm({ citizenship: data.citizenship }));
    const customerDetails = {
      firstName: states.firstName,
      middleName: states.middleName,
      lastName: states.lastName,
      emailAddress: states.email,
      telephoneNumber: states.phone,
      taxReferenceNumber: data.taxReferenceNumber,
      passportNumber: data.passportNumber,
      nationality: states.nationalityAcronym,
      dateOfBirth: states.birth,
      citizenship: data.citizenship,
    };

    const filteredData: { [key: string]: string | string[] } = {};

    for (const [key, value] of Object.entries(customerDetails)) {
      if (Array.isArray(value)) {
        if (!(value.length === 1 && value[0] === "")) {
          filteredData[key] = value.filter((item) => item !== "");
        }
      }
      if (value !== null && value !== undefined && value !== "") {
        filteredData[key] = value;
      }
    }

    if (type === "INDIVIDUAL") {
      dispatch(
        setForm({
          citizenship: data.citizenship,
          passportNumber: data.passportNumber,
          taxReferenceNumber: data.taxReferenceNumber,
          nationality: data.nationality,
        })
      );
      router.push("/individual/employment");
    } else if (type === "CORPORATION") {
      dispatch(
        setForm({
          individualCustomerData: filteredData,
        })
      );
      router.push("/individual/address");
    }
  };

  const handleSelection = (
    data: { value: string; code: string },
    name: string
  ) => {
    setInputArray((prevState) => {
      const isValueAlreadyPresent = prevState[name]?.some(
        (item) => item.value === data.value
      );

      if (isValueAlreadyPresent) {
        return prevState;
      }

      return {
        ...prevState,
        [name]: [
          ...(prevState[name] || []),
          { value: data.value, code: data.code },
        ],
      };
    });
    setCountriesCitizenship([]);
  };

  const handleDeleteItem = (key: string, value: string, code: string) => {
    setInputArray((prevState) => ({
      ...prevState,
      [key]: prevState[key]?.filter((item) => item.value !== value),
    }));
  };

  const handleNationality = (data: { value: string; code: string }) => {
    dispatch(setForm({ nationality: data.value }));
    dispatch(setForm({ nationalityAcronym: data.code }));
    setValue("nationality", data.code);
    setCountries([]);
  };

  return (
    <ScrollView contentContainerClassName="flex-grow bg-white1">
      <View className="z-50 absolute top-0 right-0 m-8">
        <ButtonLogout />
      </View>
      <View className="items-center justify-center w-full h-full">
        <Logo className="m-2" />
        <View className="m-2">
          <Title text={t("onboarding/individual/nationality:title")} />
        </View>
        <View className="items-center h-fit w-full lg:w-3/5 lg:h-152 lg:border lg:border-solid lg:border-gray lg:rounded-md lg:shadow">
          <Pressable
            onPress={() => {
              router.back();
            }}
            className="absolute z-[60] top-0 left-4 lg:left-6 lg:top-10"
          >
            <AntDesign name="arrowleft" size={24} color="#BDBDBD" />
          </Pressable>
          <View className="w-4/5 items-center mb-1 lg:w-full">
            <ProgressBarComponent progress={0.5} animatedValue={0.75} />
          </View>
          <View className="z-40 flex-grow justify-center w-full items-center">
            <Input
              label={t(
                "onboarding/individual/nationality:input.taxReferenceNumber"
              )}
              control={control}
              name="taxReferenceNumber"
              type="alpha"
            />
            <View className="z-50">
              <InputSelection
                control={control}
                name="citizenship"
                label={t("onboarding/individual/nationality:input.citizenship")}
                data={countriesCitizenship || []}
                placeholder={t("common:placeholder.country")}
                onSearchChange={(query) => setCitizenshipSearchTerm(query)}
                clearValueFunction={() => setCountriesCitizenship([])}
                handleChange={(data: { value: string; code: string }) => {
                  handleSelection(data, "citizenship");
                }}
                selectionArray={inputArray.citizenship || []}
                deleteItem={(value, code) =>
                  handleDeleteItem("citizenship", value, code)
                }
              />
            </View>
            <View className="z-40">
              <InputDropList
                label={t("onboarding/individual/nationality:input.nationality")}
                handleChange={(data) => {
                  handleNationality(data);
                }}
                clearValueFunction={() => {
                  setCountries([]);
                }}
                data={countries}
                onSearchChange={(term) => setSearchTerm(term)}
                control={control}
                name="nationality"
                placeholder={t("common:placeholder.country")}
              />
            </View>
            <Input
              label={t(
                "onboarding/individual/nationality:input.passportNumber"
              )}
              control={control}
              name="passportNumber"
              type="alpha"
              optional
            />
          </View>
          <Button
            label={t("common:button.nextstep")}
            onClick={handleSubmit(onSubmit)}
          />
        </View>
        <View className="flex-grow justify-end">
          <Copyright />
        </View>
      </View>
      {isSubmitting && <Loader />}
    </ScrollView>
  );
}
